import { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { clampWeekToSchedule, scheduleCeilingFromRows } from '@/lib/nflWeekClamp';
import { useAuth } from '@/contexts/AuthContext';
import {
  persistNflActiveLeagueId,
  readNflActiveLeagueId,
  clearNflActiveLeagueId,
  resolveActiveNflLeagueId,
} from '@/lib/nflActiveLeague';

// Re-export so existing importers (e.g. NFLStaging) keep working.
export { persistNflActiveLeagueId, NFL_ACTIVE_LEAGUE_KEY } from '@/lib/nflActiveLeague';

interface NFLLeague {
  id: string;
  name: string;
  status: string;
  team_count: number;
  commissioner_id: string;
  current_week: number;
  prize_pool: number | null;
  entry_fee: number | null;
  invite_code: string | null;
  // SAB-505: 'sabotage' (default) | 'standard' | 'half_ppr' | 'ppr'.
  scoring_type: string | null;
  // SAB-523: the league's season (e.g. 2026). Already returned by select('*').
  // SAB-824: NOT NULL, not `number | null`. The column is NOT NULL in both environments
  // as of the SAB-824 migration (prod already was; staging had diverged and was converged
  // by it). Typing it nullable is what licensed seven call sites to invent their own
  // `?? 2025` / `?? 2026` fallbacks, which disagreed with each other and could never
  // fire. Prefer `season` off the context to reaching in here.
  season_year: number;
  // Routing cutover rule (e): which draft room this league gets. Already returned by
  // select('*') like season_year -- declared here, NOT newly fetched, so the shell can
  // decide the room without a second query.
  draft_status: string | null;
  // SAB-927: the league's slot count. Same story as season_year and draft_status -- the
  // query is select('*') so this always arrived and was merely undeclared. The start-draft
  // gate needs it for the paid-league-must-be-full condition (nfl-initialize-draft:102);
  // nullable because the gate must SKIP that condition on an unknown cap rather than
  // guess one and block a startable league.
  max_teams: number | null;
  // SAB-931: which draft room this league is PINNED to, written once by
  // nfl-initialize-draft and frozen by trigger. Null = never pinned (started before the pin
  // existed), in which case nflDraftRoomChoice falls back to the old status rule.
  //
  // Deliberately NOT draft_start_time, which was the obvious candidate and is wrong: it is
  // commissioner-writable mid-draft via nfl-update-league, and it doubles as the SCHEDULED
  // draft date before a draft starts. See nflDraftRoomChoice's header.
  draft_room: string | null;
  /**
   * SAB-886: the league's jsonb settings blob, carrying `roster_slots` among much else.
   *
   * Same story as season_year, draft_status, max_teams and draft_position before it — the
   * query is select('*'), so this always arrived and was merely undeclared. Declared now
   * because five separate surfaces were each rendering a hardcoded 21-slot list instead,
   * which put IDP rows in leagues with no IDP and left a player assigned to FLEX2 with no
   * row to appear in.
   *
   * Read it through `leagueSlotOrder(league.settings?.roster_slots)`, never directly: the
   * fallback for a league with no roster_slots is load-bearing, not cosmetic.
   */
  settings: Record<string, unknown> | null;
}

interface NFLTeam {
  id: string;
  name: string;
  owner_id: string;
  league_id: string;
  wins: number;
  losses: number;
  ties: number;
  points_for: number;
  points_against: number;
  /**
   * SAB-1004: the team's logo. nfl_teams has `logo_url` and NO `avatar_url` -- verified on
   * staging and prod. This field was previously declared as `avatar_url`, so every read of
   * it was `undefined`: a declared field no query could ever populate, on the type the team
   * surfaces render from. Renamed to the column that exists.
   */
  logo_url: string | null;
  /**
   * SAB-992: whether this team is set to autodraft. Like `logo_url` above, the query is
   * select('*') so this ALWAYS arrived and was merely undeclared -- which is part of why
   * the draft room's autodraft marker reached for pick history instead of the field that
   * means it.
   *
   * nfl_teams IS in the supabase_realtime publication (verified on prod and staging), so
   * subscribers see UPDATEs to this column.
   */
  auto_draft_enabled: boolean;
  /**
   * SAB-918: the assigned snake slot, 1..N. NULL until an order is assigned.
   *
   * The query is select('*') so this always arrived; it was simply undeclared, which is
   * part of why the draft room reached for the array index instead of the field that
   * means it. Declared now so a consumer that needs draft order has something to name.
   */
  draft_position: number | null;
  /**
   * The team's REMAINING FAAB, despite the name: nfl_debit_faab DECREMENTS this column at
   * award (SAB-748) and names its own variable `v_remaining`. NFL has no `faab_remaining`
   * column and no league-level season budget — this is the only FAAB figure a team has.
   *
   * Like `logo_url` and `draft_position` above, the query is select('*') so this ALWAYS
   * arrived and was merely undeclared; the team header already renders it through an `any`
   * cast. Declared so the waiver panel can be handed it as a typed prop rather than adding
   * a query of its own.
   */
  faab_budget: number;
}

interface NFLLeagueContextType {
  leagues: NFLLeague[];
  currentLeague: NFLLeague | null;
  currentTeam: NFLTeam | null;
  currentWeek: number;
  // SAB-824: the league's season year, elevated beside currentWeek.
  //
  // Before this, currentWeek was top-level and season was not, so week was easy to get
  // right and season easy to get wrong -- every query site reached into
  // `currentLeague?.season_year` and appended its own hardcoded fallback. Two said 2025,
  // one said 2026, none could fire. That asymmetry generated SAB-811, SAB-823, SAB-824.
  //
  // `null` means NO LEAGUE IS SELECTED and nothing else. It never means "season unknown"
  // -- nfl_leagues.season_year is NOT NULL. A caller holding a league has a season; a
  // caller without one must skip the query rather than substitute a year.
  //
  // Season PHASE is deliberately absent. It is not a league-level fact: prod league
  // "Trash Test" holds 6 PRE and 84 REG matchups at once, so any single stored phase is
  // wrong for one set. Phase belongs to each read -- from the row
  // (nfl_matchups.season_type) or fixed by the domain (locks/waivers/bonuses are REG).
  // The separate "what phase is it NOW" question is SAB-901, with no query-site consumers
  // by design.
  season: number | null;
  teams: NFLTeam[];
  isLoading: boolean;
  setCurrentLeagueId: (id: string) => void;
  renameMyTeam: (newName: string) => Promise<boolean>;
  /**
   * SAB-992: re-read this league's teams. The draft room calls it when an nfl_teams UPDATE
   * arrives over realtime, so a mid-draft autodraft toggle is visible to every viewer and
   * not just the manager who flipped it.
   *
   * Exposed here rather than having the room reach for useQueryClient because this context
   * owns the ['nfl-teams', id] query -- and because ~18 draft-room test harnesses mock this
   * context, so a query client in that tree would demand a provider in all of them.
   */
  refetchTeams: () => void;
}

const NFLLeagueContext = createContext<NFLLeagueContextType | null>(null);

export function useNFLLeague() {
  const ctx = useContext(NFLLeagueContext);
  if (!ctx) throw new Error('useNFLLeague must be used within NFLLeagueProvider');
  return ctx;
}

/**
 * Same as useNFLLeague but returns null when there's no provider, instead
 * of throwing. Use this from components that may render outside an NFL
 * league context (e.g. /nfldemo) and that take a league_id prop as the
 * primary input. Falls back to context if available.
 */
export function useNFLLeagueOptional(): NFLLeagueContextType | null {
  return useContext(NFLLeagueContext);
}

/**
 * Allows injecting pre-loaded values into the NFLLeagueContext (used by demo mode).
 */
export function NFLLeagueProviderFromValues({
  children,
  leagues,
  currentLeague,
  currentTeam,
  currentWeek,
  teams,
  isLoading,
}: {
  children: ReactNode;
  leagues: NFLLeague[];
  currentLeague: NFLLeague | null;
  currentTeam: NFLTeam | null;
  currentWeek: number;
  teams: NFLTeam[];
  isLoading: boolean;
}) {
  return (
    <NFLLeagueContext.Provider value={{
      leagues,
      currentLeague,
      currentTeam,
      currentWeek,
      // SAB-824: derived from the injected league, exactly as the real provider does --
      // demo mode must not get a different season contract from the live app.
      season: currentLeague?.season_year ?? null,
      teams,
      isLoading,
      setCurrentLeagueId: () => {},
      renameMyTeam: async () => false,
      refetchTeams: () => {},
    }}>
      {children}
    </NFLLeagueContext.Provider>
  );
}

export function NFLLeagueProvider({ children }: { children: ReactNode }) {
  const { user, isLoading: authLoading } = useAuth();
  const queryClient = useQueryClient();
  const [leagues, setLeagues] = useState<NFLLeague[]>([]);
  const [currentLeagueId, setCurrentLeagueIdRaw] = useState<string | null>(null);
  // SAB-1192: the furthest week this league HAS A SCHEDULE FOR, any status.
  // Was lastCompletedWeek, which is always in the past -- see nflWeekClamp.ts.
  const [maxScheduledWeek, setMaxScheduledWeek] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const lastUserId = useRef<string | null>(null);

  // Persist on every selection (in-app switcher, create, join) so it survives a
  // reload. This is the write half of SAB-577; the read/validate half is in the
  // fetch effect below.
  const setCurrentLeagueId = useCallback((id: string) => {
    setCurrentLeagueIdRaw(id);
    persistNflActiveLeagueId(id);
  }, []);

  // ── Diagnostic logging (SAB-157) ────────────────────────────────────
  useEffect(() => {
    console.log('[NFLLeague][diag] mount', {
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      url: window.location.href,
      authLoading,
      userId: user?.id ?? null,
      userEmail: user?.email ?? null,
    });
  }, []);

  useEffect(() => {
    console.log('[NFLLeague][diag] state', {
      authLoading,
      userId: user?.id ?? null,
      leagueCount: leagues.length,
      currentLeagueId,
      isLoading,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
    });
  }, [authLoading, user, leagues, currentLeagueId, isLoading]);

  // Fetch user's NFL leagues — wait for auth to resolve first
  useEffect(() => {
    // Don't clear leagues while auth is still loading (prevents flash of empty state)
    if (authLoading) {
      console.log('[NFLLeague] Auth still loading — waiting');
      return;
    }

    if (!user) {
      console.log('[NFLLeague] No user after auth resolved — showing empty state', {
        url: window.location.href,
        viewport: `${window.innerWidth}x${window.innerHeight}`,
      });
      setLeagues([]);
      setCurrentLeagueIdRaw(null);
      clearNflActiveLeagueId();
      lastUserId.current = null;
      setIsLoading(false);
      return;
    }

    // Detect a user switch (sign out -> sign in as someone else) so we never honor
    // a persisted league id that belonged to the previous account.
    const userChanged = lastUserId.current !== null && lastUserId.current !== user.id;
    lastUserId.current = user.id;
    if (userChanged) {
      setCurrentLeagueIdRaw(null);
      clearNflActiveLeagueId();
    }

    const fetchLeagues = async () => {
      setIsLoading(true);
      try {
        console.log('[NFLLeague] Fetching leagues for user:', user.id);

        // Get league IDs from membership
        const { data: memberships, error: memErr } = await supabase
          .from('nfl_league_members' as any)
          .select('league_id')
          .eq('user_id', user.id);

        console.log('[NFLLeague] Memberships result:', { data: memberships, error: memErr });

        if (memErr) {
          console.error('[NFLLeague] Membership query error:', memErr.message, memErr.code, memErr.details);
        }

        if (!memberships?.length) {
          console.log('[NFLLeague] No memberships found — user has no NFL leagues');
          setLeagues([]);
          setIsLoading(false);
          return;
        }

        const leagueIds = memberships.map((m: any) => m.league_id);
        console.log('[NFLLeague] Found league IDs:', leagueIds);

        const { data: leagueData, error: leagueErr } = await supabase
          .from('nfl_leagues' as any)
          .select('*')
          .in('id', leagueIds)
          // SAB-577: explicit, deterministic order. The old bug was that .in() with
          // no ORDER BY returns rows in an unspecified order, so leagueData[0] was
          // effectively arbitrary and reverted the selection to the wrong league.
          .order('created_at', { ascending: true });

        console.log('[NFLLeague] Leagues result:', { data: leagueData, error: leagueErr });

        if (leagueErr) {
          console.error('[NFLLeague] League query error:', leagueErr.message, leagueErr.code);
        }

        const rows = (leagueData as unknown as NFLLeague[]) || [];
        setLeagues(rows);

        // SAB-577 resolution (pure + unit-tested in nflActiveLeague.test.ts): honor
        // the persisted selection only if it is still one of this user's leagues,
        // else the deterministic default (rows are ordered created_at asc). Persist
        // the result so it is stable next load, never an arbitrary or stale id.
        const resolved = resolveActiveNflLeagueId(
          readNflActiveLeagueId(),
          leagueIds,
          rows.map((r) => r.id),
        );
        if (resolved) setCurrentLeagueId(resolved);
      } catch (err) {
        console.error('[NFLLeague] Failed to fetch NFL leagues:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchLeagues();
    // SAB-996: keyed on the user's ID, not the user OBJECT.
    //
    // supabase-js refreshes the session when a tab regains visibility and AuthContext's
    // settle() calls setUser(session.user) on EVERY auth event, TOKEN_REFRESHED included,
    // with no identity guard. Depending on the object meant every alt-tab produced a new
    // `user` reference, re-ran this fetch, and called setLeagues with a fresh array of
    // fresh objects -- so `currentLeague = leagues.find(...)` became a new reference too.
    //
    // Downstream that is expensive: the draft room's fetchAvailablePlayers is a useCallback
    // keyed on `league`, so a new reference recreated it, re-ran the effect gated on
    // draftStatus, and fired setPlayersLoading(true) plus a paginated ~2,400-row refetch.
    // That is the alt-tab blank, and it is proved in draftFocusRefetch.test.tsx: a league
    // object with IDENTICAL CONTENTS but a new reference reloads the entire pool.
    //
    // The id is the only part this fetch actually reads, so keying on it re-runs when the
    // user genuinely changes and stays put when the same session is merely re-issued.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, authLoading]);

  // Team records (wins/losses/points_for) for the current league. Migrated from a
  // useState+useEffect load-once fetch to a keyed React Query (SAB-830 / cache-and-refetch):
  // Standings and Home read these records off `teams`, and the query is invalidated on tab
  // return (see NFLStaging) so those surfaces refresh without the provider remounting. The
  // provider mounts once at the shell, so refetchOnMount can't deliver tab-return freshness on
  // its own — the shell's invalidateQueries(['nfl-teams']) does. staleTime dedupes rapid flips;
  // React Query's default structural sharing preserves the `teams` reference when the rows are
  // unchanged, so the many `[teams]`-keyed consumer effects fire only on a real record change.
  const { data: teams = [] } = useQuery({
    queryKey: ['nfl-teams', currentLeagueId],
    enabled: !!currentLeagueId,
    refetchOnWindowFocus: true,
    staleTime: 30_000,
    queryFn: async (): Promise<NFLTeam[]> => {
      const { data, error } = await supabase
        .from('nfl_teams' as any)
        .select('*')
        .eq('league_id', currentLeagueId)
        .order('wins', { ascending: false });
      console.log('[NFLLeague] Teams result:', { count: data?.length, error });
      if (error) throw error;
      return (data as unknown as NFLTeam[]) || [];
    },
  });

  // Fetch last completed week for current league
  useEffect(() => {
    if (!currentLeagueId) {
      setMaxScheduledWeek(null);
      return;
    }

    // SAB-1192: RESET FIRST. The previous version only ever set on a hit, with no
    // else, so switching to a league with no matching rows silently KEPT the old
    // league's ceiling and clamped the new league to a week it had never played.
    // A stale value from another league is worse than no value, because no value
    // correctly means "do not clamp".
    setMaxScheduledWeek(null);

    let cancelled = false;
    const fetchMaxScheduled = async () => {
      // No status filter -- a SCHEDULED week is available data. Filtering to
      // 'completed' here is precisely what caused the week-2 lockout.
      const { data } = await supabase
        .from('nfl_matchups' as any)
        .select('week')
        .eq('league_id', currentLeagueId)
        .order('week', { ascending: false })
        .limit(1);
      if (cancelled) return;
      setMaxScheduledWeek(scheduleCeilingFromRows(data as any));
    };

    fetchMaxScheduled();
    return () => { cancelled = true; };
  }, [currentLeagueId]);

  const currentLeague = leagues.find(l => l.id === currentLeagueId) || null;
  const currentTeam = teams.find(t => t.owner_id === user?.id) || null;
  // Clamp only if current_week runs past the league's SCHEDULE -- see nflWeekClamp.ts
  // for why the old "last completed week" test locked every league out of week 2.
  const rawWeek = currentLeague?.current_week || 1;
  const currentWeek = clampWeekToSchedule(rawWeek, maxScheduledWeek);
  // SAB-824: no fallback, no default. Either a league is selected and its season is
  // known, or there is no season and callers must not query. Deliberately not `?? 2026`.
  const season = currentLeague?.season_year ?? null;

  // Rename the caller's OWN team — owner-only, exactly like MLB (LeagueContext.renameMyTeam).
  // The nfl_teams_update RLS policy (owner_id = auth.uid()) authorises the write; a direct
  // client update, no edge function or RPC. nfl_matchups has no denormalised team-name columns,
  // so unlike MLB there is nothing else to keep in sync — just the one write, then re-fetch.
  const renameMyTeam = useCallback(async (newName: string): Promise<boolean> => {
    const myTeam = teams.find(t => t.owner_id === user?.id);
    if (!myTeam || !currentLeagueId) return false;
    try {
      const { error } = await supabase
        .from('nfl_teams' as any)
        .update({ name: newName })
        .eq('id', myTeam.id);
      if (error) throw error;
      // Invalidate the keyed query rather than calling a manual re-fetch — same
      // "refetch after write" semantics, now through the cache so every consumer updates.
      await queryClient.invalidateQueries({ queryKey: ['nfl-teams', currentLeagueId] });
      return true;
    } catch (error) {
      console.error('[NFLLeague] Failed to rename team:', error);
      return false;
    }
  }, [teams, user, currentLeagueId, queryClient]);

  // SAB-992: same key renameMyTeam invalidates, so there is one way to refresh teams.
  const refetchTeams = useCallback(() => {
    if (!currentLeagueId) return;
    void queryClient.invalidateQueries({ queryKey: ['nfl-teams', currentLeagueId] });
  }, [queryClient, currentLeagueId]);

  return (
    <NFLLeagueContext.Provider value={{
      leagues,
      currentLeague,
      currentTeam,
      currentWeek,
      season,
      teams,
      isLoading,
      setCurrentLeagueId,
      renameMyTeam,
      refetchTeams,
    }}>
      {children}
    </NFLLeagueContext.Provider>
  );
}
